import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@/components/ui';
import { accountDisplayName } from './profile/accountDisplayName';
import { ResetCredentialFields } from './ResetCredentialFields';
import { StatusChip } from './shared/cells';
import { initialsOf } from './shared/cellHelpers';
import type { ResetCredential } from './useResetCredential';
import type { KeycloakAccountRow } from './types';

interface Props {
    open: boolean;
    target?: KeycloakAccountRow | null;
    ident: string;
    credential: ResetCredential;
    pending: boolean;
    error: string | null;
    onIdentChange: (value: string) => void;
    onClose: () => void;
    onSubmit: (options: { endSessions: boolean }) => void;
}

/**
 * EAA-1820 (frame 74): reset password drawer — target account card, credential
 * mode cards, I2 unlock warning, session purge checkbox, and audit footer.
 *
 * EAA-1779: the mode cards moved into `ResetCredentialFields`, shared with the
 * bulk dialog; the `credential` state is owned by the page so the submit
 * handler can read it.
 */
export function ResetAccountDrawer({
    open,
    target,
    ident,
    credential,
    pending,
    error,
    onIdentChange,
    onClose,
    onSubmit,
}: Props) {
    const { t } = useTranslation();
    const [endSessions, setEndSessions] = useState(false);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        onSubmit({ endSessions });
    };

    const adminSet = credential.mode === 'admin_set';

    const displayName = target ? accountDisplayName(target) : '';
    const identReadOnly = Boolean(target) || ident.trim().length > 0;

    let statusTone: 'ok' | 'warn' | 'off' = 'ok';
    let statusLabel = t('console.accounts.table.statusActive');
    if (target && !target.enabled) {
        statusTone = 'off';
        statusLabel = t('console.accounts.table.statusLocked');
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            className="acc-reset-drawer"
            title={
                <div className="acc-reset-drawer__head">
                    <span className="acc-reset-drawer__title">{t('console.accounts.resetModal.title')}</span>
                    <span className="acc-reset-drawer__subtitle">{t('console.accounts.resetModal.subtitle')}</span>
                </div>
            }
            width={520}
            footer={
                <div className="acc-reset-drawer__footer">
                    <span className="acc-reset-drawer__footer-note">
                        {t('console.accounts.resetModal.auditFooter')}
                    </span>
                    <button type="button" className="portal-btn" onClick={onClose} disabled={pending}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        form="accounts-reset-form"
                        className="portal-btn portal-btn-primary"
                        disabled={pending || !ident.trim() || !credential.valid}
                    >
                        {pending ? t('console.accounts.resetModal.pending') : t('console.accounts.resetModal.title')}
                    </button>
                </div>
            }
        >
            <form id="accounts-reset-form" className="acc-reset-drawer__form" onSubmit={handleSubmit}>
                {target ? (
                    <div className="acc-reset-target">
                        <span className="acc-reset-target__avatar" aria-hidden="true">
                            {initialsOf(displayName)}
                        </span>
                        <span className="acc-reset-target__main">
                            <strong className="acc-reset-target__name">{displayName}</strong>
                            <span className="acc-reset-target__meta">
                                <code>{target.username}</code>
                                {target.email && (
                                    <>
                                        <span aria-hidden="true"> · </span>
                                        {target.email}
                                    </>
                                )}
                            </span>
                        </span>
                        <StatusChip tone={statusTone} label={statusLabel} />
                    </div>
                ) : (
                    <label className="acc-reset-field">
                        {t('console.accounts.resetModal.identLabel')}
                        <input
                            value={ident}
                            onChange={(e) => onIdentChange(e.target.value)}
                            placeholder={t('console.accounts.resetModal.identPlaceholder')}
                            readOnly={identReadOnly}
                            required
                        />
                    </label>
                )}

                <ResetCredentialFields credential={credential} showEmailOption disabled={pending} />

                <div className="acc-reset-warn" role="note">
                    <svg
                        className="acc-reset-warn__icon"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        aria-hidden="true"
                    >
                        <path d="M12 4 2.5 20h19L12 4z" strokeLinejoin="round" />
                        <path d="M12 10v4M12 17h.01" strokeLinecap="round" />
                    </svg>
                    <span>
                        {t('console.accounts.resetModal.warnPrefix')}{' '}
                        <strong>{t('console.accounts.resetModal.warnBold')}</strong>{' '}
                        {t('console.accounts.resetModal.warnSuffix')}
                    </span>
                </div>

                <label className="acc-reset-sessions">
                    <input
                        type="checkbox"
                        checked={endSessions}
                        onChange={(event) => setEndSessions(event.target.checked)}
                    />
                    <span>
                        {t('console.accounts.resetModal.sessionsLabel')}
                        <span className="acc-lock-sessions-sub">{t('console.accounts.resetModal.sessionsSub')}</span>
                    </span>
                </label>

                {/* EAA-1779: the one-time-reveal warning belongs to the generated
                    mode only. In admin-set mode nothing is revealed — the operator
                    typed the value — so showing it there would warn about a panel
                    that never appears. */}
                {!adminSet && (
                    <p className="portal-accounts-onetime-warning">
                        {t('console.accounts.resetModal.onetimeWarning')}
                    </p>
                )}
                {error && <p className="portal-admin-error">{error}</p>}
            </form>
        </Drawer>
    );
}

export default ResetAccountDrawer;
